import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import InboxIcon from '@mui/icons-material/Inbox';
import SearchIcon from '@mui/icons-material/Search';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { useAuth } from '../../context/AuthContext';
import { AppProvider } from '@toolpad/core/AppProvider';
import { appTheme } from '../../theme/appTheme';
import DashboardNavigationBar from '../DashboardNavigationBar/DashboardNavigationBar';
import LoadingOverlay from '../common/LoadingOverlay';
import WizardPhotoGallery from '../common/WizardPhotoGallery/WizardPhotoGallery';
import { accountService } from '../../services/api/accountService';
import { businessLeadService } from '../../services/api/businessLeadService';
import { WIZARD_ADDITIONAL_DETAILS_FIELD_IDS_KEY } from '../../utils/serviceWizardFormData';
import {
  DashboardContainer,
  SidebarContainer,
  ContentWrapper,
} from '../BusinessDashboard/BusinessDashboard.styled';
import {
  PageWrapper,
  HeaderRow,
  PageHeading,
  SubHeading,
  SectionHeading,
  TableScroller,
  ServiceRequestTable,
  TableHead,
  Th,
  Tr,
  Td,
  ServiceTitle,
  ServiceSubtitle,
  LocationCell,
  UrgencyBadge,
  StatusBadge,
  StatusActions,
  StatusActionButton,
  NoActionsLabel,
  ActionErrorText,
  ContactName,
  ContactDetail,
  PreferredContactBadge,
  ExpandToggleButton,
  DetailRow,
  DetailCell,
  DetailPanel,
  DetailSection,
  DetailSectionTitle,
  DetailNote,
  DetailGrid,
  DetailItem,
  DetailLabel,
  DetailValue,
  EmptyState,
  SearchBarRow,
  SearchInput,
  SearchHint,
} from './ServiceRequests.styled';

const humanize = (value) =>
  typeof value === 'string'
    ? value
        .split('-')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    : '';

const formatReceivedDate = (iso) =>
  iso
    ? new Date(iso).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : '—';

const STATUS_LABELS = {
  pending: 'Pending',
  accepted: 'Accepted',
  declined: 'Declined',
  completed: 'Completed',
};

const CLOSED_STATUSES = new Set(['declined', 'completed']);

function leadMatchesContactSearch(lead, query) {
  const q = query.trim().toLowerCase();
  if (!q) return true;

  const requester = lead.requester;
  if (!requester) return false;

  const fullName = [requester.firstName, requester.lastName].filter(Boolean).join(' ');
  const phoneDigits = String(requester.phone ?? '').replace(/\D/g, '');
  const queryDigits = q.replace(/\D/g, '');

  if (queryDigits.length >= 3 && phoneDigits.includes(queryDigits)) {
    return true;
  }

  const haystack = [fullName, requester.firstName, requester.lastName, requester.email, requester.phone]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return haystack.includes(q);
}

const ServiceRequests = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [businessId, setBusinessId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [updatingLeadId, setUpdatingLeadId] = useState(null);
  const [actionError, setActionError] = useState('');
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [contactSearch, setContactSearch] = useState('');

  /* ─── Auth guard (mirrors PaymentInvoices) ─── */
  useEffect(() => {
    if (!user) return;
    if (user?.Role !== 'business_user' && user?.Role !== 'admin_user') {
      navigate('/');
    }
  }, [user, navigate]);

  /* ─── Load this business's leads from the backend ─── */
  useEffect(() => {
    let isCancelled = false;

    const loadLeads = async () => {
      setIsLoading(true);
      setError('');
      try {
        const account = await accountService.getAccount();
        if (isCancelled) return;

        if (!account.BusinessId) {
          setError('No business associated with your account.');
          return;
        }

        setBusinessId(account.BusinessId);
        const data = await businessLeadService.listByBusiness(
          account.BusinessId,
        );
        if (!isCancelled) setLeads(data || []);
      } catch (err) {
        if (!isCancelled) {
          setError(err.message || 'Could not load your service requests. Please try again.');
        }
      } finally {
        if (!isCancelled) setIsLoading(false);
      }
    };

    loadLeads();
    return () => {
      isCancelled = true;
    };
  }, []);

  const sortedLeads = useMemo(
    () =>
      [...leads].sort(
        (a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime(),
      ),
    [leads],
  );

  const filteredLeads = useMemo(
    () => sortedLeads.filter((lead) => leadMatchesContactSearch(lead, contactSearch)),
    [sortedLeads, contactSearch],
  );

  const openLeads = useMemo(
    () => filteredLeads.filter((lead) => !CLOSED_STATUSES.has(lead.status)),
    [filteredLeads],
  );

  const closedLeads = useMemo(
    () => filteredLeads.filter((lead) => CLOSED_STATUSES.has(lead.status)),
    [filteredLeads],
  );

  const handleUpdateStatus = async (lead, status) => {
    if (!businessId) return;
    setUpdatingLeadId(lead.id);
    setActionError('');
    try {
      await businessLeadService.updateStatus(lead.id, businessId, status);
      const data = await businessLeadService.listByBusiness(businessId);
      setLeads(data || []);
    } catch (err) {
      setActionError(err.message || 'Could not update service request status. Please try again.');
    } finally {
      setUpdatingLeadId(null);
    }
  };

  const toggleExpand = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const renderLeadRow = (lead) => {
    const isExpanded = expandedIds.has(lead.id);
    const qaEntries = Object.entries(lead.additionalDetails || {}).filter(
      ([question]) => question !== WIZARD_ADDITIONAL_DETAILS_FIELD_IDS_KEY,
    );
    const hasCustomTarget =
      lead.inquiryKind === 'custom' && (lead.targetServiceTitle || lead.targetSubServiceType);

    return (
      <React.Fragment key={lead.id}>
        <Tr>
          <Td>
            <ExpandToggleButton
              type="button"
              onClick={() => toggleExpand(lead.id)}
              aria-label={
                isExpanded ? 'Collapse service request details' : 'Expand service request details'
              }
              aria-expanded={isExpanded}
            >
              {isExpanded ? (
                <ExpandLessIcon fontSize="small" />
              ) : (
                <ExpandMoreIcon fontSize="small" />
              )}
            </ExpandToggleButton>
          </Td>
          <Td>{formatReceivedDate(lead.sentAt)}</Td>
          <Td>
            <ContactName>
              {lead.requester?.firstName} {lead.requester?.lastName}
            </ContactName>
            {lead.requester?.preferredContactMethod === 'phone' && (
              <PreferredContactBadge>Prefers a call</PreferredContactBadge>
            )}
            {lead.requester?.preferredContactMethod === 'text' && (
              <PreferredContactBadge>Prefers a text</PreferredContactBadge>
            )}
            {lead.requester?.preferredContactMethod === 'email' && (
              <PreferredContactBadge>Prefers an email</PreferredContactBadge>
            )}
            {lead.requester?.phone && (
              <ContactDetail>{lead.requester.phone}</ContactDetail>
            )}
            {lead.requester?.email && (
              <ContactDetail>{lead.requester.email}</ContactDetail>
            )}
          </Td>
          <Td>
            <ServiceTitle>
              {humanize(lead.serviceType)} · {humanize(lead.subServiceType)}
            </ServiceTitle>
            <ServiceSubtitle>{lead.requestSummary?.problemSummary}</ServiceSubtitle>
          </Td>
          <Td>
            <LocationCell>
              <LocationOnIcon style={{ fontSize: '0.95rem' }} />
              {[lead.city, lead.state].filter(Boolean).join(', ') || '—'}
            </LocationCell>
          </Td>
          <Td>
            <UrgencyBadge $urgency={lead.urgency}>
              {lead.requestSummary?.urgency || humanize(lead.urgency)}
            </UrgencyBadge>
          </Td>
          <Td>
            <StatusBadge $status={lead.status}>
              {STATUS_LABELS[lead.status] || humanize(lead.status) || 'Pending'}
            </StatusBadge>
          </Td>
          <Td>
            {lead.status === 'pending' && (
              <StatusActions>
                <StatusActionButton
                  type="button"
                  $tone="accept"
                  disabled={updatingLeadId === lead.id}
                  onClick={() => handleUpdateStatus(lead, 'accepted')}
                >
                  <CheckIcon /> Accept
                </StatusActionButton>
                <StatusActionButton
                  type="button"
                  $tone="decline"
                  disabled={updatingLeadId === lead.id}
                  onClick={() => handleUpdateStatus(lead, 'declined')}
                >
                  <CloseIcon /> Decline
                </StatusActionButton>
              </StatusActions>
            )}

            {lead.status === 'accepted' && (
              <StatusActions>
                <StatusActionButton
                  type="button"
                  $tone="complete"
                  disabled={updatingLeadId === lead.id}
                  onClick={() => handleUpdateStatus(lead, 'completed')}
                >
                  <DoneAllIcon /> Mark Completed
                </StatusActionButton>
              </StatusActions>
            )}

            {(lead.status === 'declined' || lead.status === 'completed') && (
              <NoActionsLabel>—</NoActionsLabel>
            )}
          </Td>
        </Tr>

        {isExpanded && (
          <DetailRow>
            <DetailCell colSpan={8}>
              <DetailPanel>
                {qaEntries.length > 0 && (
                  <DetailSection>
                    <DetailSectionTitle>Additional Details</DetailSectionTitle>
                    <DetailGrid>
                      {qaEntries.map(([question, answer]) => (
                        <DetailItem key={question}>
                          <DetailLabel>{question}</DetailLabel>
                          <DetailValue>{String(answer)}</DetailValue>
                        </DetailItem>
                      ))}
                    </DetailGrid>
                  </DetailSection>
                )}

                {lead.details && (
                  <DetailSection>
                    <DetailSectionTitle>Customer Notes</DetailSectionTitle>
                    <DetailNote>{lead.details}</DetailNote>
                  </DetailSection>
                )}

                {lead.photos?.length > 0 && (
                  <DetailSection>
                    <DetailSectionTitle>Photos</DetailSectionTitle>
                    <WizardPhotoGallery urls={lead.photos} inline />
                  </DetailSection>
                )}

                {hasCustomTarget && (
                  <DetailSection>
                    <DetailSectionTitle>Requested Service</DetailSectionTitle>
                    <DetailGrid>
                      {lead.targetServiceTitle && (
                        <DetailItem>
                          <DetailLabel>Service</DetailLabel>
                          <DetailValue>{lead.targetServiceTitle}</DetailValue>
                        </DetailItem>
                      )}
                      {lead.targetSubServiceType && (
                        <DetailItem>
                          <DetailLabel>Sub-service</DetailLabel>
                          <DetailValue>{humanize(lead.targetSubServiceType)}</DetailValue>
                        </DetailItem>
                      )}
                    </DetailGrid>
                  </DetailSection>
                )}
              </DetailPanel>
            </DetailCell>
          </DetailRow>
        )}
      </React.Fragment>
    );
  };

  const serviceRequestTableHead = (
    <TableHead>
      <Tr>
        <Th aria-label="Expand" $width="4%" />
        <Th $width="11%">Received</Th>
        <Th $width="16%">Contact</Th>
        <Th $width="17%">Service</Th>
        <Th $width="10%">Location</Th>
        <Th $width="16%">Urgency</Th>
        <Th $width="10%">Status</Th>
        <Th $width="16%">Actions</Th>
      </Tr>
    </TableHead>
  );

  return (
    <AppProvider branding={{ title: 'Service Requests' }} theme={appTheme}>
      <DashboardContainer>
        <SidebarContainer>
          <DashboardNavigationBar />
        </SidebarContainer>

        <ContentWrapper>
          <PageWrapper>
            <HeaderRow>
              <div>
                <PageHeading>Service Requests</PageHeading>
                <SubHeading>Customer service requests routed to your business.</SubHeading>
              </div>
            </HeaderRow>

            {error && (
              <EmptyState>
                <p>{error}</p>
              </EmptyState>
            )}

            {actionError && <ActionErrorText>{actionError}</ActionErrorText>}

            {!error && !isLoading && leads.length > 0 && (
              <SearchBarRow>
                <SearchIcon aria-hidden />
                <SearchInput
                  type="search"
                  value={contactSearch}
                  onChange={(e) => setContactSearch(e.target.value)}
                  placeholder="Search by name, email, or phone..."
                  aria-label="Search service requests by contact information"
                />
              </SearchBarRow>
            )}

            {!error && !isLoading && leads.length > 0 && contactSearch.trim() && (
              <SearchHint>
                {filteredLeads.length === 0
                  ? `No service requests match "${contactSearch.trim()}".`
                  : `Showing ${filteredLeads.length} of ${leads.length} service request${leads.length === 1 ? '' : 's'}.`}
              </SearchHint>
            )}

            {!error && !isLoading && leads.length === 0 && (
              <EmptyState>
                <InboxIcon style={{ fontSize: '2rem', opacity: 0.4 }} />
                <p>No service requests yet. New customer requests will appear here.</p>
              </EmptyState>
            )}

            {!error && leads.length > 0 && filteredLeads.length === 0 && contactSearch.trim() && (
              <EmptyState>
                <p>No service requests match your contact search.</p>
              </EmptyState>
            )}

            {!error && filteredLeads.length > 0 && (
              <>
                <SectionHeading>Open Service Requests</SectionHeading>
                {openLeads.length > 0 ? (
                  <TableScroller>
                    <ServiceRequestTable>
                      {serviceRequestTableHead}
                      <tbody>{openLeads.map(renderLeadRow)}</tbody>
                    </ServiceRequestTable>
                  </TableScroller>
                ) : (
                  <EmptyState>
                    <p>No open service requests right now.</p>
                  </EmptyState>
                )}

                {closedLeads.length > 0 && (
                  <>
                    <SectionHeading>Closed Service Requests</SectionHeading>
                    <TableScroller>
                      <ServiceRequestTable>
                        {serviceRequestTableHead}
                        <tbody>{closedLeads.map(renderLeadRow)}</tbody>
                      </ServiceRequestTable>
                    </TableScroller>
                  </>
                )}
              </>
            )}
          </PageWrapper>
        </ContentWrapper>
      </DashboardContainer>

      {isLoading && <LoadingOverlay />}
    </AppProvider>
  );
};

export default ServiceRequests;
